import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import KollelIntroScreen from './KollelIntroScreen';
import NewKollelForm from './NewKollelForm';

interface NewKollelDialogProps {
  fundId?: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Shell of the "כולל חדש" wizard: intro screen → 4-step form → success screen (HAT-1446 phase 3),
 * matching the designer's variants (Figma node 5242:76249's "Property 1" symbols).
 *
 * The form stays mounted while the intro shows (hidden via display, not unmounted) for the same
 * reason NewProjectDialog does this: "חזרה להתחלה" must not throw away what was typed. Do NOT
 * collapse this to `hasStarted ? <NewKollelForm/> : <KollelIntroScreen/>` - see NewProjectDialog's
 * comment, the same trap applies here unchanged.
 */
const NewKollelDialog = ({ fundId, open, onClose, onSuccess }: NewKollelDialogProps) => {
  const { t } = useTranslation();
  const [hasStarted, setHasStarted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const handleClose = () => {
    setHasStarted(false);
    setIsSuccess(false);
    setFormKey((key) => key + 1);
    onClose();
  };

  const handleSubmitted = () => {
    setIsSuccess(true);
    onSuccess();
  };

  const handleAddAnother = () => {
    setIsSuccess(false);
    setHasStarted(false);
    setFormKey((key) => key + 1);
  };

  if (isSuccess) {
    return (
      <MainDialogLayout
        open={open}
        onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}
        title={t('kollels.wizard.dialogTitle')}
      >
        <SuccessScreen
          title={t('kollels.wizard.success.title')}
          description={t('kollels.wizard.success.subtitle')}
          primaryButtonText={t('kollels.wizard.success.backToList')}
          primaryButtonAction={handleClose}
          secondaryButtonText={t('kollels.wizard.success.addAnother')}
          secondaryButtonAction={handleAddAnother}
        />
      </MainDialogLayout>
    );
  }

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}
      title={t('kollels.wizard.dialogTitle')}
    >
      {!hasStarted && <KollelIntroScreen onStart={() => setHasStarted(true)} />}
      <div style={hasStarted ? { display: 'contents' } : { display: 'none' }}>
        <NewKollelForm
          key={formKey}
          fundId={fundId}
          onSuccess={handleSubmitted}
          onBackToIntro={() => setHasStarted(false)}
        />
      </div>
    </MainDialogLayout>
  );
};

export default NewKollelDialog;
